/** Building blocks that only the Hosting screens use (Figma "Hosting — …" frames, HST-01). */
import type { ReactNode } from "react";

import { cx } from "@/components/ui";

/** A Figma icon from public/icons/hosting, at the size it was drawn. */
export function HostingIcon({ name, size, className }: { name: string; size: number; className?: string }) {
  return (
    <img
      src={`/icons/hosting/${name}.svg`}
      alt=""
      width={size}
      height={size}
      className={cx("shrink-0", className)}
    />
  );
}

/** The page heading: 44px icon tile, 26px title (+ status), subtitle, actions on the right. */
export function HostingHead({
  title,
  status,
  subtitle,
  actions,
}: {
  title: ReactNode;
  status?: ReactNode;
  subtitle: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted">
        <HostingIcon name="server" size={20} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2.5">
          <h1 className="truncate text-[26px] font-bold text-fg">{title}</h1>
          {status}
        </div>
        <p className="text-sm text-fg-subtle">{subtitle}</p>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
    </div>
  );
}

/** A card's heading: 32px icon tile, 16px title, something on the right. */
export function CardHead({ icon, title, aside }: { icon: string; title: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-[9px] border border-border bg-brand-50">
        <HostingIcon name={icon} size={16} />
      </div>
      <h2 className="text-base font-bold text-fg">{title}</h2>
      <span className="flex-1" />
      {aside}
    </div>
  );
}

/** A labelled usage bar whose value is text ("2 of 5 sites") and whose fill is a plain fraction. */
export function UsageMeter({ label, value, fraction }: { label: string; value: string; fraction: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2">
        <span className="text-[13px] font-semibold text-fg">{label}</span>
        <span className="flex-1" />
        <span className="text-[12.5px] text-fg-subtle">{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-2 rounded-full bg-[#12a97e]"
          style={{ width: `${Math.min(100, Math.max(fraction > 0 ? 2 : 0, fraction * 100))}%` }}
        />
      </div>
    </div>
  );
}

/** Coral 12.5px text link ("Manage domains →", "Restore →"). */
export const textLink = "text-[12.5px] font-semibold whitespace-nowrap text-brand-500 hover:underline";
